import React from 'react';
import useGameStore from '@store/useGameStore';
import useMetadataStore from '@store/useMetadataStore';
import UnitIcon from '@gameobjects/UnitIcon';
import ActionIcon from '@components/ui/game/action-icon/ActionIcon';
import XhelosTooltip from '@components/ui/general/xhelos-tooltip/XhelosTooltip';
import CoordinateLink from '@components/ui/general/CoordinateLink';
import { getEffectIcon, STAT_ICONS } from '@utils/statIcons';
import './EventCard.css';

/**
 * Reusable play button overlay component
 * Shows play icon on hover when event is selected
 * onClick bubbles to parent card by default (no stopPropagation)
 */
const PlayButtonOverlay = ({ show, isArtificial }) => {
  if (!show || isArtificial) return null;

  return (
    <div className="play-button-overlay">
      <svg className="play-icon" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z"/>
      </svg>
    </div>
  );
};

/**
 * Info button component - triggers tooltip on click
 * Separated from card click to avoid blocking map view
 *
 * Note: stopPropagation is on the outer div, NOT the button itself.
 * This allows floating-ui's click handler (via getReferenceProps) to work
 * while still preventing the click from bubbling to the card.
 */
const InfoButton = ({ tooltipContent }) => (
  <div className="card-info-button-wrapper" onClick={(e) => e.stopPropagation()}>
    <XhelosTooltip
      content={tooltipContent}
      placement="top"
      theme="dark"
      hasArrow={true}
      trigger="click"
      interactive={true}
    >
      <button
        className="card-info-button"
        onClick={() => console.log('[InfoButton] clicked')}
      >
        ?
      </button>
    </XhelosTooltip>
  </div>
);

/**
 * Event Card Component for Cinematic Timeline
 * Handles different event types: actions, effects, etc.
 * Displays unit icon on top and event-specific icon below
 */
const EventCard = ({ event, index, isSelected, isExecuted, isAnimating, showPlayButton, onClick }) => {
  
  // Determine event type
  const eventType = event.type; // Default to action for backward compatibility
  const isArtificial = event.isArtificial;
  
  // Common card states
  const isCurrentlyAnimating = isAnimating;
  const canShowPlayButton = showPlayButton; // CSS will handle hover visibility
  
  // Base classes for all event types
  const getCardState = () => {
    if (isSelected) return 'active';
    if (isExecuted) return 'completed';
    return 'pending';
  };
  
  const cardState = getCardState();
  const baseCardClasses = [
    'timeline-event-card',
    cardState,
    isCurrentlyAnimating ? 'animating' : '',
    isArtificial ? 'artificial' : '',
    event.teamClass ? `team-${event.teamClass}` : '',
    eventType === 'effect_processing' || eventType === 'effect_application' || eventType === 'effect_removal' ? 'effect-event' : ''
  ].filter(Boolean).join(' ');
  
  // Get entity information for card display
  // For artificial events (current_state, etc.), card_entities is optional
  // For real events (actions, effects), FAIL LOUD if missing
  const cardEntities = event.card_entities || [];

  if (!isArtificial && cardEntities.length === 0) {
    throw new Error(`[EventCard] Missing card_entities for non-artificial event type: ${eventType}`);
  }

  const isMultiTarget = cardEntities.length > 1;

  // Validation: Single entity must be 'unit' type (constructions not yet implemented)
  // Only validate for real events with entities
  if (!isArtificial && cardEntities.length === 1 && cardEntities[0].type !== 'unit') {
    throw new Error(`NonImplemented: Entity type '${cardEntities[0].type}' not supported yet. Only 'unit' is currently implemented.`);
  }

  // Get primary entity for single-target cards
  const primaryEntity = cardEntities.length === 1 && cardEntities[0].type === 'unit'
    ? useGameStore.getState().getUnitById(cardEntities[0].id)
    : null;
  //console.log('[EventCard] Rendering event:', event);
 /* if(!event.isArtificial){
    console.log('[EventCard] Rendering unit:', unit);
    console.log('[EventCard] Rendering unit id:', unit.id);
  }*/
  // Handle click - parent component manages the logic
  const handleClick = (e) => {
    console.log('[EventCard] handleClick called', { index, eventType, isAnimating, onClick: !!onClick });
    if (!isAnimating) {
      onClick();
    }
  };

  // ============ SHARED HELPERS ============
  // These are used by both renderActionEvent and renderEffectEvent

  // Helper to render Lucide stat icon
  const renderStatIcon = (stat, size = 14) => {
    const IconComponent = STAT_ICONS[stat] || STAT_ICONS.default;
    return <IconComponent size={size} />;
  };

  // Helper to get unit name from store
  const getUnitName = (unitId) => {
    const unit = useGameStore.getState().getUnitById(unitId);
    return unit?.unit_template?.name || unit?.name || `Unit`;
  };

  // Helper to get effect name from metadata
  const getEffectName = (slug) => {
    const effect = useMetadataStore.getState().getEffectBySlug(slug);
    return effect?.name || slug;
  };

  // Helper to render failure reason text/JSX
  const renderFailureReason = (target) => {
    if (!target.failure_reason) return null;

    const failureLabels = {
      'miss': 'MISS!',
      'dodge': 'DODGE!',
      'block': 'BLOCKED!',
      'immunity': 'IMMUNE!',
      'already_dead': 'ALREADY DEAD',
      'max_hp': 'FULL HP',
      'no_damage': 'NO DAMAGE'
    };

    // Special handling for already_has_effect - show which effect
    if (target.failure_reason === 'already_has_effect') {
      const effectName = target.failure_data?.effect_name || 'effect';
      return `Already has ${effectName}`;
    }

    const label = failureLabels[target.failure_reason] || target.failure_reason;

    // For block, show damage vs defense with icons
    if (target.failure_reason === 'block' && target.failure_data) {
      const damage = target.failure_data.damage_before_defense;
      const defense = target.failure_data.defense_value;
      const damageType = target.failure_data.damage_type || 'strength';

      if (damage !== undefined && defense !== undefined) {
        const DamageIcon = STAT_ICONS[damageType] || STAT_ICONS.strength;
        const DefenseIcon = STAT_ICONS.armor;
        const damageColor = damageType === 'psi' ? 'var(--color-damage-psi-text)' : 'var(--color-red)';

        return (
          <span className="tooltip-blocked-detail">
            {label} (
            <span className="blocked-value">{damage}</span>
            <DamageIcon size={14} style={{ color: damageColor }} />
            <span className="blocked-vs">vs</span>
            <span className="blocked-value">{defense}</span>
            <DefenseIcon size={14} style={{ color: 'var(--color-cyan)' }} />
            )
          </span>
        );
      }
    }

    return label;
  };

  // Switch based on event type
  switch (eventType) {
    case 'action':
      return renderActionEvent();
    case 'effect_processing':
    case 'effect_application':
    case 'effect_removal':
      return renderEffectEvent();
    case 'current_state':
      return renderCurrentStateEvent();
    default:
      return renderUnknownEvent();
  }
  
  // ================ RENDER FUNCTIONS ================
  
  // Render action event (move, attack, etc.)
  function renderActionEvent() {
    // Guard clause - Check if we have required data
    if (!isArtificial && !event.action_id) {
      console.warn('[EventCard] Missing action_id for action event:', event);
      return renderUnknownEvent();
    }
    
    // Get action definition using action_id
    let actionDefinition = null;
    let actionName = 'Unknown Action';
    let actionSlug = 'unknown';
    
    if (!isArtificial) {
      actionDefinition = useGameStore.getState().getActionById(event.action_id);
      if (!actionDefinition) {
        console.warn('[EventCard] Action definition not found for action_id:', event.action_id, {
          event,
          availableActions: Object.keys(useGameStore.getState().game?.actions || {})
        });
      } else {
        actionName = actionDefinition.name;
        actionSlug = actionDefinition.slug;
        
        // If this is a reaction/counter, add "Counter: " prefix
        if (event.is_reaction) {
          actionName = `Counter: ${actionName}`;
        }
      }
    } else {
      // For artificial events, use the provided name
      actionName = event.action?.name || 'State';
    }
  
  // Check if this was a missed attack (legacy check from resolution_details)
  const wasMissed = event.resolution_details?.action === 'attack_missed' ||
                    event.resolution_details?.was_missed === true ||
                    event.resolution_details?.missed === true;

  // ============ CARDINFO SYSTEM (SSOT) ============
  // All target info comes from backend's cardinfo - no more merging from multiple sources
  const cardinfo = event.cardinfo || { origin: null, targets: [] };
  const targets = cardinfo.targets || [];

  // Tooltip content - Redesigned for clarity and information
  const tooltipContent = (
    <div className="action-tooltip">
      {/* === HEADER: Action name + costs === */}
      <div className="action-tooltip-header">
        <div className="action-tooltip-title-row">
          <ActionIcon
            action={actionDefinition}
            actionId={actionSlug}
            size={24}
            className="action-tooltip-icon"
            showFallback={true}
          />
          <span className="action-tooltip-name">{actionName}</span>
          {event.is_reaction && <span className="action-tooltip-badge badge-counter">Counter</span>}
        </div>
        {/* Costs row */}
        {(event.ap_cost > 0 || event.energy_cost > 0) && (
          <div className="action-tooltip-costs">
            {event.ap_cost > 0 && (
              <span className="cost-chip cost-ap">
                {event.ap_cost} {renderStatIcon('ap', 12)}
              </span>
            )}
            {event.energy_cost > 0 && (
              <span className="cost-chip cost-energy">
                {event.energy_cost} ⚡
              </span>
            )}
          </div>
        )}
      </div>

      {/* === CASTER: Who performed the action === */}
      {primaryEntity && (
        <div className="action-tooltip-caster">
          <span className="tooltip-section-label">Caster</span>
          <div className="caster-info">
            <UnitIcon unitId={primaryEntity.id} size={28} clickable={false} />
            <div className="caster-details">
              <span className="caster-name">{getUnitName(primaryEntity.id)}</span>
              <span className="caster-id">#{primaryEntity.id}</span>
            </div>
          </div>
        </div>
      )}

      {/* === DESTINATION: For Move actions, show where unit moved to === */}
      {event.unit_moved && event.target_data && (
        <div className="action-tooltip-destination">
          <span className="tooltip-section-label">Destination</span>
          <div className="destination-info">
            <div className={`destination-tile team-${event.teamClass || 'own'}`}>
              <span className="destination-arrow">→</span>
            </div>
            <div className="destination-coords">
              <CoordinateLink x={event.target_data.x} y={event.target_data.y} />
            </div>
          </div>
        </div>
      )}

      {/* === TARGETS: What happened to each target === */}
      {targets.length > 0 && (
        <div className="action-tooltip-targets">
          <span className="tooltip-section-label">
            {targets.length === 1 ? 'Target' : `Targets (${targets.length})`}
          </span>
          <div className="targets-list">
            {targets.map(target => {
              const hasChanges = target.stat_deltas && Object.values(target.stat_deltas).some(v => v !== 0);
              const hasFailure = !!target.failure_reason;
              const hasEffects = target.effects_added?.length > 0;

              return (
                <div key={target.id} className={`target-row ${hasFailure ? 'target-failed' : ''} ${target.died ? 'target-died' : ''}`}>
                  {/* Target identity */}
                  <div className="target-identity">
                    <UnitIcon unitId={target.id} size={24} clickable={false} />
                    <div className="target-info">
                      <span className="target-name">{getUnitName(target.id)}</span>
                      <span className="target-id">#{target.id}</span>
                    </div>
                  </div>

                  {/* Target results */}
                  <div className="target-results">
                    {/* Stat changes */}
                    {hasChanges && (
                      <div className="target-stat-changes">
                        {Object.entries(target.stat_deltas).map(([stat, delta]) => (
                          delta !== 0 && (
                            <span key={stat} className={`stat-change ${delta > 0 ? 'stat-positive' : 'stat-negative'}`}>
                              {delta > 0 ? '+' : ''}{delta} {renderStatIcon(stat, 14)}
                            </span>
                          )
                        ))}
                      </div>
                    )}

                    {/* Failure reason with explanation */}
                    {hasFailure && (
                      <div className="target-failure">
                        <span className="failure-badge">{renderFailureReason(target)}</span>
                      </div>
                    )}

                    {/* Effects applied */}
                    {hasEffects && (
                      <div className="target-effects">
                        {target.effects_added.map((slug, idx) => (
                          <span key={idx} className="effect-applied-tag">
                            {getEffectIcon(slug)} {getEffectName(slug)}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Death indicator */}
                    {target.died && (
                      <div className="target-death">
                        <span className="death-badge">💀 Killed</span>
                      </div>
                    )}

                    {/* Success with no visible changes (like buffs) */}
                    {!hasChanges && !hasFailure && !hasEffects && !target.died && (
                      <div className="target-success">
                        <span className="success-badge">✓</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={baseCardClasses} onClick={(e) => { console.log('[EventCard] DIV clicked directly', e.target); handleClick(e); }}>
      {/* Step indicator */}
      <div className="card-step-indicator">{index}</div>

      {/* Info button - only for non-artificial cards */}
      {!isArtificial && <InfoButton tooltipContent={tooltipContent} />}

      {/* Main Icon - Large centered (20px = 1.25rem) */}
      <div className="card-main-icon">
        <ActionIcon
          action={actionDefinition}
          actionId={actionSlug}
          size={20}
          className={`timeline-action-icon ${isCurrentlyAnimating ? 'animating' : ''}`}
          teamClass={event.teamClass}
          showFallback={true}
        />
      </div>

      {/* Name */}
      <span className="card-name">{actionName}</span>

      {/* Unit Icon (small) + Unit ID */}
      <div className="card-unit-section">
        {primaryEntity ? (
          <>
            <UnitIcon unitId={primaryEntity.id} size={24} clickable={false} />
            <span className="card-unit-id">#{primaryEntity.id}</span>
          </>
        ) : (
          <div className="unknown-unit">❓</div>
        )}
      </div>

      {/* Subtitle */}
      <div className="card-subtitle">
        {event.is_reaction ? 'Counter' : 'Action'}
      </div>

      {/* Play Button Overlay - shows on hover when selected */}
      <PlayButtonOverlay show={canShowPlayButton} isArtificial={isArtificial} />
    </div>
  );
  }
  
  // Render effect event (unified for processing, application, and removal)
  function renderEffectEvent() {
    // Check if this is a merged "Unit Turn Start" RQ
    const isMergedTurnStartRQ = event.effect_slug === 'unit_turn_start' || event.effect_details?.is_merged;

    // Get effect definition from metadata store (skip for merged RQs)
    const effectDefinition = isMergedTurnStartRQ ? null : useMetadataStore.getState().getEffectBySlug(event.effect_slug);
    const effectName = isMergedTurnStartRQ ? '' : (effectDefinition?.name || event.effect_slug || 'Effect');
    const effectIcon = isMergedTurnStartRQ ? '⏰' : getEffectIcon(event.effect_slug);

    // Get display name from card_label - FAIL LOUD if missing
    if (!event.card_label) {
      throw new Error(`[EventCard] Missing card_label for effect event: ${event.effect_slug}`);
    }
    const displayName = event.card_label;

    // Determine event type badge
    const getEffectTypeBadge = () => {
      switch (eventType) {
        case 'effect_application': return { label: 'Applied', className: 'badge-applied' };
        case 'effect_removal': return { label: 'Expired', className: 'badge-expired' };
        case 'effect_processing': return { label: 'Tick', className: 'badge-tick' };
        default: return null;
      }
    };
    const typeBadge = getEffectTypeBadge();

    // Use cardinfo for targets (same as action tooltips)
    const effectCardinfo = event.cardinfo || { origin: null, targets: [] };
    const effectTargets = effectCardinfo.targets || [];

    // For Turn Start, get the unit ID from effect_details or cardEntities
    const turnStartUnitId = isMergedTurnStartRQ
      ? (event.effect_details?.unit_id || cardEntities[0]?.id)
      : null;

    // Tooltip content - Redesigned to match action tooltip style
    const tooltipContent = (
      <div className="effect-tooltip">
        {/* === HEADER: Effect name + badge === */}
        <div className="effect-tooltip-header-redesigned">
          <div className="effect-tooltip-title-row">
            <span className="effect-tooltip-icon-large">{effectIcon}</span>
            <span className="effect-tooltip-name-large">{effectName || displayName}</span>
            {typeBadge && (
              <span className={`effect-tooltip-badge ${typeBadge.className}`}>{typeBadge.label}</span>
            )}
          </div>
          {/* Duration chip if applicable */}
          {event.on_turn_start_counters !== undefined && event.on_turn_start_counters > 0 && (
            <div className="effect-tooltip-meta">
              <span className="duration-chip">
                {event.on_turn_start_counters} turn{event.on_turn_start_counters > 1 ? 's' : ''} ⏱️
              </span>
            </div>
          )}
        </div>

        {/* === TURN START: Show which unit's turn === */}
        {isMergedTurnStartRQ && turnStartUnitId && (
          <div className="effect-tooltip-unit-turn">
            <span className="tooltip-section-label">Unit Turn</span>
            <div className="turn-unit-info">
              <UnitIcon unitId={turnStartUnitId} size={28} clickable={false} />
              <div className="turn-unit-details">
                <span className="turn-unit-name">{getUnitName(turnStartUnitId)}</span>
                <span className="turn-unit-id">#{turnStartUnitId}</span>
              </div>
            </div>
          </div>
        )}

        {/* === SOURCE: Who has/applied the effect === */}
        {!isMergedTurnStartRQ && primaryEntity && (
          <div className="effect-tooltip-source">
            <span className="tooltip-section-label">Source</span>
            <div className="source-info">
              <UnitIcon unitId={primaryEntity.id} size={28} clickable={false} />
              <div className="source-details">
                <span className="source-name">{getUnitName(primaryEntity.id)}</span>
                <span className="source-id">#{primaryEntity.id}</span>
              </div>
            </div>
          </div>
        )}

        {/* === TARGETS: What happened to each target === */}
        {effectTargets.length > 0 && (
          <div className="effect-tooltip-targets">
            <span className="tooltip-section-label">
              {effectTargets.length === 1 ? 'Target' : `Targets (${effectTargets.length})`}
            </span>
            <div className="targets-list">
              {effectTargets.map(target => {
                const hasChanges = target.stat_deltas && Object.values(target.stat_deltas).some(v => v !== 0);
                const hasFailure = !!target.failure_reason;
                const hasEffectsAdded = target.effects_added?.length > 0;
                const hasEffectsRemoved = target.effects_removed?.length > 0;

                return (
                  <div key={target.id} className={`target-row ${hasFailure ? 'target-failed' : ''} ${target.died ? 'target-died' : ''}`}>
                    {/* Target identity */}
                    <div className="target-identity">
                      <UnitIcon unitId={target.id} size={24} clickable={false} />
                      <div className="target-info">
                        <span className="target-name">{getUnitName(target.id)}</span>
                        <span className="target-id">#{target.id}</span>
                      </div>
                    </div>

                    {/* Target results */}
                    <div className="target-results">
                      {/* Stat changes */}
                      {hasChanges && (
                        <div className="target-stat-changes">
                          {Object.entries(target.stat_deltas).map(([stat, delta]) => (
                            delta !== 0 && (
                              <span key={stat} className={`stat-change ${delta > 0 ? 'stat-positive' : 'stat-negative'}`}>
                                {delta > 0 ? '+' : ''}{delta} {renderStatIcon(stat, 14)}
                              </span>
                            )
                          ))}
                        </div>
                      )}

                      {/* Failure reason with explanation */}
                      {hasFailure && (
                        <div className="target-failure">
                          <span className="failure-badge">{renderFailureReason(target)}</span>
                        </div>
                      )}

                      {/* Effects applied */}
                      {hasEffectsAdded && (
                        <div className="target-effects">
                          {target.effects_added.map((slug, idx) => (
                            <span key={idx} className="effect-applied-tag">
                              {getEffectIcon(slug)} {getEffectName(slug)}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Effects removed */}
                      {hasEffectsRemoved && (
                        <div className="target-effects-removed">
                          {target.effects_removed.map((slug, idx) => (
                            <span key={idx} className="effect-removed-tag">
                              ✕ {getEffectName(slug)}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Death indicator */}
                      {target.died && (
                        <div className="target-death">
                          <span className="death-badge">💀 Killed</span>
                        </div>
                      )}

                      {/* Success with no visible changes */}
                      {!hasChanges && !hasFailure && !hasEffectsAdded && !hasEffectsRemoved && !target.died && (
                        <div className="target-success">
                          <span className="success-badge">✓</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );

    return (
      <div className={baseCardClasses} onClick={handleClick}>
        {/* Step indicator */}
        <div className="card-step-indicator">{index}</div>

        {/* Info button */}
        <InfoButton tooltipContent={tooltipContent} />

        {/* Main Icon - Large centered */}
        <div className="card-main-icon">
          <span className="effect-icon-large">{effectIcon}</span>
        </div>

        {/* Effect Name - show displayName for merged RQs */}
        <span className="card-name">{effectName || displayName}</span>

        {/* Unit Icon (small) + Unit ID */}
        <div className="card-unit-section">
          {isMultiTarget ? (
            <div className="multi-target-icon">Ⓜ️</div>
          ) : primaryEntity ? (
            <>
              <UnitIcon unitId={primaryEntity.id} size={24} clickable={false} />
              <span className="card-unit-id">#{primaryEntity.id}</span>
            </>
          ) : cardEntities.length === 1 ? (
            <>
              <UnitIcon unitId={cardEntities[0].id} size={24} clickable={false} />
              <span className="card-unit-id">#{cardEntities[0].id}</span>
            </>
          ) : (
            <div className="unknown-unit">❓</div>
          )}
        </div>

        {/* Subtitle: Trigger type */}
        <div className="card-subtitle">{displayName}</div>

        {/* Play Button Overlay - shows on hover when selected */}
        <PlayButtonOverlay show={canShowPlayButton} isArtificial={isArtificial} />
      </div>
    );
  }

  // Render current state event (shows current game state)
  function renderCurrentStateEvent() {
    const tooltipContent = (
      <div className="card-tooltip-content">
        <div className="card-tooltip-content-name">Current Game State</div>
        <div className="card-tooltip-content-row">This represents the current state of the game</div>
        <div className="card-tooltip-content-row">All previous events have been executed</div>
      </div>
    );

    return (
      <div className={baseCardClasses} onClick={handleClick}>
        {/* No step indicator for current state card */}

        {/* Info button */}
        <InfoButton tooltipContent={tooltipContent} />

        {/* Main Icon - Large centered */}
        <div className="card-main-icon">
          <span className="effect-icon-large">🎯</span>
        </div>

        {/* Name */}
        <span className="card-name">Current</span>

        {/* Spacer for visual balance */}
        <div className="card-unit-section">
          <span className="effect-icon-large">✨</span>
        </div>

        {/* Play Button Overlay */}
        <PlayButtonOverlay show={canShowPlayButton} isArtificial={true} />
      </div>
    );
  }

  // Render unknown/unsupported event type
  function renderUnknownEvent() {
    const tooltipContent = (
      <div className="card-tooltip-content">
        <div className="card-tooltip-content-name">Unknown Event</div>
        <div className="card-tooltip-content-row">Type: {eventType}</div>
        <div className="card-tooltip-content-row">⚠️ This event type is not yet implemented</div>
      </div>
    );

    return (
      <div className={baseCardClasses} onClick={handleClick}>
        {/* Step indicator */}
        <div className="card-step-indicator">{index}</div>

        {/* Info button */}
        <InfoButton tooltipContent={tooltipContent} />

        {/* Main Icon - Large centered */}
        <div className="card-main-icon">
          <span className="effect-icon-large">⚠️</span>
        </div>

        {/* Name */}
        <span className="card-name">{eventType}</span>

        {/* Unit Icon (small) */}
        <div className="card-unit-section">
          {isMultiTarget ? (
            <div className="multi-target-icon">Ⓜ️</div>
          ) : primaryEntity ? (
            <UnitIcon unitId={primaryEntity.id} size={24} clickable={false} />
          ) : cardEntities.length === 1 ? (
            <UnitIcon unitId={cardEntities[0].id} size={24} clickable={false} />
          ) : (
            <div className="unknown-unit">❓</div>
          )}
        </div>

        {/* Subtitle */}
        <div className="card-subtitle">Unknown</div>

        {/* Play Button Overlay - shows on hover when selected */}
        <PlayButtonOverlay show={canShowPlayButton} isArtificial={isArtificial} />
      </div>
    );
  }
};

export default React.memo(EventCard);